import React from 'react';

import { Select } from '@components/common';

export interface ValueSelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

// Style constants
const SELECT_STYLES = {
  base: 'bg-white hover:bg-white',
  chip: 'flex items-center rounded border border-indigo-300 bg-indigo-50 px-2 py-1 text-sm text-indigo-700',
  chipButton: 'focus:outline-none ml-1 text-indigo-500 hover:text-indigo-700'
} as const;

// Base props interface
interface BaseValueSelectProps {
  options: ValueSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
}

// Single select props
interface SingleValueSelectProps extends BaseValueSelectProps {
  multi: false;
  value: string;
  onChange: (value: string) => void;
}

// Multi select props
interface MultiValueSelectProps extends BaseValueSelectProps {
  multi: true;
  value: string | string[];
  onChange: (value: string[]) => void;
}

// Union type for all props
type ValueSelectProps = SingleValueSelectProps | MultiValueSelectProps;

export const ValueSelect: React.FC<ValueSelectProps> = (props) => {
  const { options, placeholder = 'Select option...', disabled = false, error = false, multi = false } = props;

  // Single select (default)
  if (!multi) {
    const singleProps = props as SingleValueSelectProps;
    return (
      <Select
        options={options}
        value={singleProps.value}
        onChange={singleProps.onChange}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        className={SELECT_STYLES.base}
      />
    );
  }

  // Multi-select with chips
  const multiProps = props as MultiValueSelectProps;
  const selectedValues = Array.isArray(multiProps.value) ? multiProps.value : [];
  const availableOptions = options.filter((opt) => !selectedValues.includes(opt.value));

  const handleSelect = (selected: string) => {
    if (!selectedValues.includes(selected)) {
      multiProps.onChange([...selectedValues, selected]);
    }
  };

  const handleRemove = (removed: string) => {
    multiProps.onChange(selectedValues.filter((v) => v !== removed));
  };

  return (
    <div>
      <Select
        options={availableOptions}
        value={''}
        onChange={handleSelect}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        className={SELECT_STYLES.base}
      />
      <div className='mt-2 flex flex-wrap gap-2'>
        {selectedValues.map((val) => {
          const opt = options.find((o) => o.value === val);
          return (
            <span key={val} className={SELECT_STYLES.chip}>
              {opt?.label || val}
              <button type='button' className={SELECT_STYLES.chipButton} onClick={() => handleRemove(val)}>
                ×
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
};
